import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  CheckCircle2,
  DollarSign,
  Lock,
  Loader2,
  Undo2,
  X,
  FileText,
  Send,
} from 'lucide-react';

/**
 * iter971k-Z · Turn 5 - Edit-mode banner for CreateBillPage.
 *
 * Restores the legacy EditBillPage banner inside the unified
 * Create-in-edit-mode view. Mirrors EditInvoiceStatusBanner but talks to
 * the bill endpoints.
 */
const EditBillStatusBanner = ({
  bill,
  api,
  onChanged,
  getLocalCurrencySymbol,
  formatDisplayNumber,
}) => {
  const [removingPayment, setRemovingPayment] = useState(false);
  const [approving, setApproving] = useState(false);

  if (!bill) return null;

  const status = (bill.status || '').toLowerCase();
  const amountPaid = Number(bill.amount_paid || 0);
  const amount = Number(bill.amount || bill.total_amount || 0);
  const balanceDue = Number(bill.balance_due ?? Math.max(0, amount - amountPaid));
  const hasPayments = amountPaid > 0;
  const isDraft = status === 'draft';
  const isFullyPaid = status === 'paid' || (amount > 0 && amountPaid >= amount);
  const isPartiallyPaid = !isFullyPaid && amountPaid > 0;
  const isReconciled = Boolean(bill.is_reconciled || bill.reconciliation_id || bill.reconciled);
  const paymentHistory = bill.payment_history || bill.payments || [];

  const symbol = typeof getLocalCurrencySymbol === 'function' ? getLocalCurrencySymbol() : '';
  const fmt = typeof formatDisplayNumber === 'function' ? formatDisplayNumber : (n) => Number(n || 0).toFixed(2);

  const removeSinglePayment = async (paymentId) => {
    if (isReconciled) {
      toast.error('Cannot remove payment - this bill has been reconciled');
      return;
    }
    setRemovingPayment(true);
    try {
      await axios.delete(`${api}/bills/${bill.id}/payments/${paymentId}`);
      toast.success('Payment removed and journals reversed');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove payment');
    } finally {
      setRemovingPayment(false);
    }
  };

  const removeAllPayments = async () => {
    if (isReconciled) {
      toast.error('Cannot remove payments - this bill has been reconciled');
      return;
    }
    setRemovingPayment(true);
    try {
      await axios.post(`${api}/bills/${bill.id}/remove-all-payments`);
      toast.success('All payments removed and journals reversed');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove payments');
    } finally {
      setRemovingPayment(false);
    }
  };

  const approveDraft = async () => {
    setApproving(true);
    try {
      await axios.patch(`${api}/bills/${bill.id}/approve`);
      toast.success('Bill approved: recognition journal posted.');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve bill');
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="edit-bill-status-banner">
      {/* Draft banner */}
      {isDraft && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium text-gray-800">Draft</p>
                <p className="text-sm text-gray-600">This bill has not been approved. No GL entries have been posted.</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={approveDraft}
              disabled={approving || bill?.asset_type === 'amortizable_prepaid'}
              data-testid="approve-draft-bill-btn"
              className="gap-2"
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Approve Bill
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Reconciled read-only lock */}
      {isReconciled && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Reconciled with bank</p>
              <p className="text-sm text-blue-700">
                Financial fields are locked. Unreconcile this bill from the bank feed to edit or remove payments.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment progress + actions */}
      {(isPartiallyPaid || isFullyPaid) && (
        <Card className={isFullyPaid ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {isFullyPaid ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <DollarSign className="w-5 h-5 text-amber-600" />
                )}
                <div>
                  <p className={`font-medium ${isFullyPaid ? 'text-green-800' : 'text-amber-800'}`}>
                    {isFullyPaid ? 'Fully Paid' : 'Partially Paid'}
                  </p>
                  <p className={`text-sm ${isFullyPaid ? 'text-green-700' : 'text-amber-700'}`}>
                    {symbol}{fmt(amountPaid)} of {symbol}{fmt(amount)} paid
                    {!isFullyPaid && ` • Balance: ${symbol}${fmt(balanceDue)}`}
                  </p>
                </div>
              </div>
              {hasPayments && !isReconciled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={removeAllPayments}
                  disabled={removingPayment}
                  className="gap-2 border-red-500 text-red-700 hover:bg-red-100"
                  data-testid="remove-all-payments-btn"
                >
                  {removingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                  Remove All Payments
                </Button>
              )}
            </div>

            {hasPayments && !isReconciled && (
              <div className="mt-3 p-3 bg-amber-100 rounded-lg flex items-start gap-2">
                <Lock className="w-4 h-4 text-amber-700 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Editing locked:</strong> Financial fields are locked because this bill has payments. Remove
                  payments below to edit amounts and line items.
                </p>
              </div>
            )}

            {paymentHistory.length > 0 && (
              <div className="mt-4 pt-4 border-t border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-2">Payment History</p>
                <div className="space-y-2">
                  {paymentHistory.map((payment, idx) => (
                    <div
                      key={payment.id || idx}
                      className={`flex justify-between items-center text-sm p-2 rounded-lg ${
                        payment.is_voided ? 'bg-gray-100 opacity-60' : 'bg-white/50'
                      }`}
                    >
                      <div>
                        <span className={payment.is_voided ? 'text-gray-500 line-through' : 'text-amber-700'}>
                          {payment.payment_date}: {payment.reference || 'Payment'}
                        </span>
                        <span className={`font-medium ml-2 ${payment.is_voided ? 'text-gray-500 line-through' : 'text-amber-800'}`}>
                          {symbol}{fmt(payment.amount)}
                        </span>
                        {payment.is_voided && <span className="ml-2 text-xs text-red-500 font-medium">VOIDED</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        {!isReconciled && payment.id && !payment.is_voided && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSinglePayment(payment.id)}
                            disabled={removingPayment}
                            className="text-red-600 hover:text-red-800 hover:bg-red-100 h-7 px-2"
                            data-testid={`void-payment-${idx}`}
                            title="Void this payment"
                          >
                            {removingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isDraft && !isPartiallyPaid && !isFullyPaid && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary" className="capitalize">{bill.status}</Badge>
          <span>Bill {bill.bill_number || ''}</span>
        </div>
      )}
    </div>
  );
};

export default EditBillStatusBanner;
